import BlackList, { BlackListTable } from "@/components/blacklist";
import MoreInfoButton from "@/components/more-info-button";
import StyledTooltip from "@/components/styled-tooltip";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Info, Mail } from "lucide-react";
import {
  MXServerBlacklist,
  DomainBlacklist,
  SMTPBlacklist,
} from "@/types/postman.types";
import { Badge } from "@/components/ui/badge";

const EmailHealthBlacklist = ({
  domainBlacklist,
  mxServerBlacklist,
  smtpBlacklist,
}: {
  domainBlacklist: DomainBlacklist[];
  mxServerBlacklist: MXServerBlacklist[];
  smtpBlacklist: SMTPBlacklist[];
}) => {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-lg font-semibold">Blacklists</h2>
        <StyledTooltip description="Shows the status of your emails on various blacklists. This helps identify if your emails are being blocked by any email providers.">
          <Info className="h-4 w-4 text-muted-foreground" />
        </StyledTooltip>
        <MoreInfoButton slug="blacklist-guide" />
      </div>
      <div className="grid gap-4">
        {/* Domain Blacklists Section */}
        <BlackList type="domainBlacklist" data={domainBlacklist || []} />

        {/* MX Server Blacklists */}
        <BlackList type="mxServerBlacklist" data={mxServerBlacklist || []} />

        {/* SMTP Server Blacklists - Already at the bottom */}
        <SMTPBlacklistComponent data={smtpBlacklist || []} />
      </div>
    </div>
  );
};

const SMTPBlacklistComponent = ({ data }: { data: SMTPBlacklist[] }) => {
  return (
    <Collapsible defaultOpen>
      <Card>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="p-4 hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-orange-500" />
                SMTP Server Blacklists
                <StyledTooltip description="SMTP Server Blacklists check if your email sending servers are listed on spam databases. Being blacklisted can prevent your emails from being delivered, as receiving mail servers often reject messages from blacklisted SMTP servers.">
                  <Info className="w-3 h-3 text-muted-foreground" />
                </StyledTooltip>
              </CardTitle>
              <div className="flex items-center gap-3">
                <MoreInfoButton slug="smtp-server-blacklist" />
                <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-200 group-[data-state=open]>svg]:rotate-180" />
              </div>
            </div>
            <CardDescription className="self-start">
              Email sending behavior and authentication
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="p-0">
            <div className="space-y-2">
              {data?.map((serverData, index) => {
                serverData.blacklists.sort((a, b) => {
                  // Listed entries come first
                  if (a.listed && !b.listed) return -1;
                  if (!a.listed && b.listed) return 1;

                  // Then entries with responses
                  if (a.response && !b.response) return -1;
                  if (!a.response && b.response) return 1;

                  // Otherwise maintain original order
                  return 0;
                });

                const listedCount = serverData.blacklists.filter(
                  (b) => b.listed
                ).length;

                return (
                  <Collapsible key={serverData.server}>
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-3 hover:bg-muted/50">
                        <div className="flex items-center gap-2">
                          <ChevronDown className="h-4 w-4 text-slate-600" />
                          <span className="font-medium text-sm">
                            SMTP Server {index + 1}
                          </span>
                          <span className="text-sm text-slate-600">
                            {serverData.server}
                          </span>
                        </div>
                        <Badge
                          variant="outline"
                          className={
                            listedCount === 0
                              ? "text-green-500 bg-green-500/10 border-none font-normal"
                              : "bg-red-500/10 text-red-500 border-none font-normal"
                          }
                        >
                          {listedCount === 0
                            ? "All Clear"
                            : `${listedCount} Issues Found`}
                        </Badge>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <BlackListTable blacklists={serverData.blacklists} />
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default EmailHealthBlacklist;
